import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../../context/ThemeContext';
import { useAuthContext } from '../../context/AuthContext';
import { changePasswordApi } from '../../utils/apis';
import { isAuthenticatedViaCookie, getUserInfoFromLocalStorage } from '../../utils/utils';
import Sidebar from '../linkpayment/Sidebar';
import MobileMenuButton from '../linkpayment/MobileMenuButton';
import { InPortalLayoutContext } from '../linkpayment/PortalLayout';
import '../linkpayment/LinkPayment.css';

const PASSWORD_HINT =
  'At least 8 characters, with an uppercase letter, a lowercase letter, a number, and a special character.';

export default function Setting() {
  const { theme } = useTheme();
  const { isAuthenticated } = useAuthContext();
  const navigate = useNavigate();
  const inLayout = useContext(InPortalLayoutContext);

  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [userInfo, setUserInfo] = useState<any>(null);

  const toggleSidebar = () => setIsSidebarOpen((v) => !v);
  const closeSidebar = () => setIsSidebarOpen(false);

  useEffect(() => {
    if (!isAuthenticated && !isAuthenticatedViaCookie()) {
      navigate('/login');
      return;
    }
    setUserInfo(getUserInfoFromLocalStorage());
  }, [isAuthenticated, navigate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (!currentPassword || !newPassword || !confirmPassword) {
      setError('All fields are required.');
      return;
    }
    if (newPassword !== confirmPassword) {
      setError('New passwords do not match.');
      return;
    }
    if (newPassword.length < 8) {
      setError('Password must be at least 8 characters long.');
      return;
    }
    if (currentPassword === newPassword) {
      setError('New password cannot be the same as the current password.');
      return;
    }

    try {
      setLoading(true);
      // changePasswordApi returns null on failure (e.g. wrong current password / expired session).
      const response = await changePasswordApi(currentPassword, newPassword);
      if (!response) {
        setError('Failed to change password. Please check your current password and try again.');
        return;
      }
      setSuccess('Password changed successfully!');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } finally {
      setLoading(false);
    }
  };

  const inputClass =
    'w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring focus:ring-blue-300';

  return (
    <div className="flex flex-col lg:flex-row min-h-screen">
      {/* Branded portal chrome (sidebar + mobile header) is St. Luke-only, and rendered here only when
          not already inside PortalLayout. Gating on the theme keeps a non-St.Luke tenant that hits the
          default-group /setting route (inLayout=false) from getting St. Luke branding. */}
      {!inLayout && theme.client === 'stluke' && <Sidebar userInfo={userInfo} isOpen={isSidebarOpen} onClose={closeSidebar} />}

      <div className="grow">
        {/* Mobile Header (St. Luke-only chrome; see the sidebar gate above) */}
        {!inLayout && theme.client === 'stluke' && (
        <div className="lg:hidden bg-white border-b border-gray-200 shadow-sm sticky top-0 z-30">
          <div className="px-4 py-3 flex items-center justify-between flex-row-reverse">
            <div className="flex items-center">
              <h1 className="text-lg font-bold text-gray-900">St. Luke Hospital</h1>
            </div>
            <MobileMenuButton isOpen={isSidebarOpen} onToggle={toggleSidebar} />
          </div>
        </div>
        )}

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="bg-white rounded-xl shadow-lg p-6 mb-6 max-w-md mx-auto">
            <h2 className="text-2xl font-bold text-gray-900 mb-6 text-center">Change Password</h2>

            {error && (
              <div className="bg-red-100 text-red-700 p-3 rounded mb-4 text-sm">{error}</div>
            )}
            {success && (
              <div className="bg-green-100 text-green-700 p-3 rounded mb-4 text-sm">{success}</div>
            )}

            <form onSubmit={handleSubmit}>
              <div className="mb-4">
                <label className="block text-gray-700 font-medium mb-2" htmlFor="currentPassword">
                  Current Password
                </label>
                <input
                  type="password"
                  id="currentPassword"
                  className={inputClass}
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                />
              </div>
              <div className="mb-2">
                <label className="block text-gray-700 font-medium mb-2" htmlFor="newPassword">
                  New Password
                </label>
                <input
                  type="password"
                  id="newPassword"
                  className={inputClass}
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                />
              </div>
              <p className="text-xs text-gray-500 mb-4">{PASSWORD_HINT}</p>
              <div className="mb-6">
                <label className="block text-gray-700 font-medium mb-2" htmlFor="confirmPassword">
                  Confirm New Password
                </label>
                <input
                  type="password"
                  id="confirmPassword"
                  className={inputClass}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                />
              </div>
              <button
                type="submit"
                disabled={loading}
                className={`w-full text-white py-2 px-4 rounded-lg focus:outline-none focus:ring disabled:opacity-50 ${theme['button-bg']}`}
              >
                {loading ? 'Updating…' : 'Change Password'}
              </button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}
